<script setup lang="ts">
/**
 * The bar across the top: the logo, the menu bar and the document's title.
 *
 * Web: the desktop drew its own caption buttons in a frameless window. A
 * browser tab has the browser's, so there are none here. Installed as an app
 * with Window Controls Overlay, the bar sits in the title bar area and leaves
 * room for the caption buttons the system draws, as the desktop did.
 */
import { computed, watch, watchEffect } from 'vue'
import { activeDoc, isDirty } from '../stores/documents'
import { useThemeStore } from '../stores/theme'
import MenuBar from './MenuBar.vue'
import logoMark from '../assets/logo-mark.png'

const title = computed(() => {
  const d = activeDoc.value
  if (!d) return 'ekram.md'
  return `${isDirty(d) ? '• ' : ''}${d.name} — ekram.md`
})

/**
 * Keep the tab's title in step.
 *
 * `watchEffect`, not `computed`: a computed is lazy, so one whose only purpose
 * is a side effect never runs if nothing reads it.
 */
watchEffect(() => {
  document.title = title.value
})

/**
 * The browser's own bar takes the theme's colour too: the address bar on a
 * phone, the title bar of the installed app.
 */
const themes = useThemeStore()
watch(
  () => themes.applied,
  () => {
    const bg = getComputedStyle(document.documentElement).getPropertyValue('--chrome-bg').trim()
    if (!bg) return
    for (const meta of document.querySelectorAll('meta[name="theme-color"]')) meta.remove()
    const meta = document.createElement('meta')
    meta.name = 'theme-color'
    meta.content = bg
    document.head.appendChild(meta)
  },
  { immediate: true }
)
</script>

<template>
  <header class="titlebar">
    <div class="titlebar__left">
      <!-- The tab's title already names the app, so the logo is decoration. -->
      <span class="titlebar__mark" aria-hidden="true">
        <img class="titlebar__logo" :src="logoMark" alt="" draggable="false" />
      </span>
      <MenuBar />
    </div>

    <div class="titlebar__drag">
      <span class="titlebar__title">{{ title }}</span>
    </div>
  </header>
</template>

<style scoped>
.titlebar {
  display: flex;
  align-items: stretch;
  height: 26px;
  background: var(--chrome-bg);
  color: var(--chrome-fg);
  border-bottom: 1px solid var(--chrome-border);
  -webkit-app-region: drag;
  flex: none;
}
.titlebar__left {
  display: flex;
  align-items: stretch;
  -webkit-app-region: no-drag;
}
.titlebar__mark {
  display: grid;
  place-items: center;
  width: 30px;
}
/*
 * The logo on a small tile of its own paper colour. Most themes' title bars are
 * dark, and the logo's dark strokes would disappear on them; on the tile it
 * reads the same under every theme, in its own colours.
 */
.titlebar__logo {
  width: 18px;
  height: 18px;
  padding: 1px;
  border-radius: 4px;
  background: #fefdfb;
  box-sizing: border-box;
}
.titlebar__drag {
  flex: 1;
  display: grid;
  place-items: center;
  min-width: 0;
}
.titlebar__title {
  font-size: 12px;
  color: var(--chrome-fg-dim);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/*
 * Installed with Window Controls Overlay: the bar is the title bar, and leaves
 * the system's caption buttons the room it is given. Last, so it wins.
 */
@media (display-mode: window-controls-overlay) {
  .titlebar {
    padding-right: calc(100vw - env(titlebar-area-x, 0px) - env(titlebar-area-width, 100vw));
    height: env(titlebar-area-height, 26px);
    app-region: drag;
  }
}
</style>
